/**
 * EtlRunsPanel — Dashboard ETL Runs (qa_etl_runs).
 *
 * Endpoints:
 *  - GET /api/admin/etl-runs?limit=50 → { data, total, health } (admin+)
 *  - GET /api/admin/etl-runs/health/:script → { healthy, stale_hours, last } (público)
 *  - POST /api/admin/etl-runs → registrar run manual (admin+)
 *
 * Features:
 *  - Tabla con runs recientes + status badges + duración.
 *  - Health summary cards por script_name (chequeo stale > 72h).
 *  - Filtro por script_name (dropdown).
 *  - Auto-refresh cada 30s.
 *  - Modal "Registrar run manual".
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_BASE } from '../config';
import './EtlRunsPanel.css';

const KNOWN_SCRIPTS = [
  'refresh_matching_result.sql',
  'price_normalize.sql',
  'maestro_sync.sql',
  'catalog_backfill.sql',
];
const STATUS_OPTIONS = ['running', 'completed', 'error', 'partial'];
const REFRESH_INTERVAL_MS = 30_000;

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (_) { return '—'; }
}

function calcDuration(startedAt, finishedAt) {
  if (!startedAt) return '—';
  if (!finishedAt) return 'en curso';
  try {
    const start = new Date(startedAt).getTime();
    const end = new Date(finishedAt).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return '—';
    const ms = end - start;
    if (ms < 1000) return `${ms} ms`;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m}m ${rs}s`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  } catch (_) { return '—'; }
}

function StatusBadge({ status }) {
  const norm = (status || '').toLowerCase();
  const cls = norm === 'completed' || norm === 'success' ? 'etl-status-success'
    : norm === 'running' ? 'etl-status-running'
    : norm === 'error' || norm === 'failed' ? 'etl-status-error'
    : norm === 'partial' ? 'etl-status-partial'
    : 'etl-status-other';
  return <span className={`etl-status ${cls}`}>{status || '—'}</span>;
}

function ToastBanner({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

function RegisterRunModal({ defaultEmail, knownScripts, onClose, onSubmit, busy }) {
  const [useCustomScript, setUseCustomScript] = useState(false);
  const [scriptName, setScriptName] = useState(knownScripts[0] || '');
  const [customScript, setCustomScript] = useState('');
  const [triggeredBy, setTriggeredBy] = useState(defaultEmail || 'manual');
  const [status, setStatus] = useState('completed');
  const [rowsAffected, setRowsAffected] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [details, setDetails] = useState('');
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const finalScript = useCustomScript ? customScript.trim() : scriptName;
    if (!finalScript) {
      setFormError('script_name es requerido');
      return;
    }
    let detailsObj = null;
    if (details.trim()) {
      try {
        detailsObj = JSON.parse(details);
      } catch (err) {
        setFormError('details debe ser JSON válido (o dejarlo vacío)');
        return;
      }
    }
    const payload = {
      script_name: finalScript,
      triggered_by: triggeredBy.trim() || 'manual',
      status,
      rows_affected: rowsAffected === '' ? null : Number(rowsAffected),
      error_message: errorMessage.trim() || null,
      details: detailsObj,
    };
    try {
      await onSubmit(payload);
    } catch (err) {
      setFormError(err.message || 'Error al registrar run');
    }
  };

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header">
            <h3>Registrar run manual</h3>
          </div>
          <div className="um-modal-body">
            {formError && <div className="um-error" style={{ marginBottom: 12 }}>{formError}</div>}

            <div className="um-field">
              <label>Script</label>
              {!useCustomScript ? (
                <select value={scriptName} onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setUseCustomScript(true);
                  } else {
                    setScriptName(e.target.value);
                  }
                }}>
                  {knownScripts.map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="__custom__">Otro…</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={customScript}
                    onChange={(e) => setCustomScript(e.target.value)}
                    placeholder="mi_script_custom.sql"
                    required
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="um-btn um-btn-ghost"
                    onClick={() => { setUseCustomScript(false); setCustomScript(''); }}
                  >
                    ←
                  </button>
                </div>
              )}
            </div>

            <div className="um-field">
              <label>Triggered by</label>
              <input
                type="text"
                value={triggeredBy}
                onChange={(e) => setTriggeredBy(e.target.value)}
                placeholder="usuario@dominio.com o 'cron'"
              />
            </div>

            <div className="um-field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="um-field">
              <label>Rows affected</label>
              <input
                type="number"
                value={rowsAffected}
                onChange={(e) => setRowsAffected(e.target.value)}
                placeholder="(opcional)"
                min="0"
              />
            </div>

            <div className="um-field">
              <label>Error message</label>
              <textarea
                value={errorMessage}
                onChange={(e) => setErrorMessage(e.target.value)}
                placeholder="(opcional, solo si status=error)"
                rows={2}
              />
            </div>

            <div className="um-field">
              <label>Details (JSON)</label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder='(opcional) ej: {"source":"manual","note":"..."}'
                rows={3}
              />
            </div>
          </div>
          <div className="um-modal-footer">
            <button type="button" className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="um-btn um-btn-primary" disabled={busy}>
              {busy ? 'Registrando…' : 'Registrar run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EtlRunsPanel({ currentEmail }) {
  const [data, setData] = useState([]);
  const [health, setHealth] = useState([]); // del endpoint /api/admin/etl-runs
  const [healthExtra, setHealthExtra] = useState({}); // map script -> { healthy, stale_hours, last }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [scriptFilter, setScriptFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalBusy, setModalBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const refreshTimerRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchRuns = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (scriptFilter) params.set('script', scriptFilter);
      const res = await fetch(`${API_BASE}/api/admin/etl-runs?${params.toString()}`);
      if (res.status === 403) throw new Error('No tenés permisos (se requiere admin+).');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(Array.isArray(json.data) ? json.data : []);
      setHealth(Array.isArray(json.health) ? json.health : []);
    } catch (err) {
      setError(err.message || 'Error al cargar ETL runs');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [scriptFilter]);

  // Auto-refresh cada 30s
  useEffect(() => {
    fetchRuns(false);
    refreshTimerRef.current = setInterval(() => fetchRuns(true), REFRESH_INTERVAL_MS);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [fetchRuns]);

  // Fetch healthExtra para cada script único en la lista (best-effort).
  useEffect(() => {
    const scriptNames = Array.from(new Set(health.map(h => h.script_name).filter(Boolean)));
    if (scriptNames.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(scriptNames.map(async (name) => {
        try {
          const res = await fetch(`${API_BASE}/api/admin/etl-runs/health/${encodeURIComponent(name)}`);
          if (!res.ok) return;
          const j = await res.json();
          updates[name] = j;
        } catch (_) {
          // ignore
        }
      }));
      if (!cancelled) setHealthExtra(updates);
    })();
    return () => { cancelled = true; };
  }, [health]);

  const uniqueScriptsInData = useMemo(() => {
    const s = new Set();
    for (const r of data) if (r.script_name) s.add(r.script_name);
    for (const h of health) if (h.script_name) s.add(h.script_name);
    return Array.from(s).sort();
  }, [data, health]);

  const handleRegister = async (payload) => {
    setModalBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/etl-runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 403) throw new Error('No tenés permisos para registrar runs.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setModalOpen(false);
      showToast(`Run de ${payload.script_name} registrado`);
      await fetchRuns(true);
    } catch (err) {
      throw err;
    } finally {
      setModalBusy(false);
    }
  };

  return (
    <>
      <div className="um-card">
        <div className="um-toolbar">
          <div className="um-toolbar-left">
            <select
              className="um-search"
              value={scriptFilter}
              onChange={(e) => setScriptFilter(e.target.value)}
              style={{ width: 'auto', padding: '6px 10px' }}
            >
              <option value="">Todos los scripts</option>
              {uniqueScriptsInData.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span className="um-count">
              {loading ? '…' : `${data.length} runs`}
            </span>
            <span className="etl-refresh-hint">Auto-refresh cada 30s</span>
          </div>
          <div>
            <button
              className="um-btn um-btn-ghost"
              onClick={() => fetchRuns(false)}
              disabled={loading}
              style={{ marginRight: 8 }}
            >
              Refrescar
            </button>
            <button
              className="um-btn um-btn-primary"
              onClick={() => setModalOpen(true)}
            >
              + Registrar run manual
            </button>
          </div>
        </div>

        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

        {/* Health summary cards */}
        {health.length > 0 && (
          <div className="etl-health-grid">
            {health.map(h => {
              const extra = healthExtra[h.script_name] || {};
              const stale = extra.healthy === false;
              return (
                <div key={h.script_name} className={`etl-health-card ${stale ? 'stale' : ''}`}>
                  <div className="etl-health-name">{h.script_name}</div>
                  <div className="etl-health-row">
                    <span>Último éxito:</span>
                    <span className="etl-health-val">{fmtDateTime(h.last_success)}</span>
                  </div>
                  <div className="etl-health-row">
                    <span>Último intento:</span>
                    <span className="etl-health-val">{fmtDateTime(h.last_attempt)}</span>
                  </div>
                  <div className="etl-health-row">
                    <span>OK / Error:</span>
                    <span className="etl-health-val">
                      <span className="etl-ok">{h.success_count ?? 0}</span>
                      {' / '}
                      <span className="etl-err">{h.error_count ?? 0}</span>
                    </span>
                  </div>
                  {extra.healthy === false ? (
                    <span className="etl-health-badge stale">
                      STALE {extra.stale_hours ? `(${extra.stale_hours}h)` : ''}
                    </span>
                  ) : extra.healthy === true ? (
                    <span className="etl-health-badge ok">
                      OK {extra.stale_hours != null ? `(${extra.stale_hours}h)` : ''}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <div className="um-table-wrap">
          {loading && data.length === 0 ? (
            <div className="um-loading">Cargando ETL runs…</div>
          ) : data.length === 0 ? (
            <div className="um-empty">No hay runs registrados.</div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>Script</th>
                  <th>Triggered by</th>
                  <th>Started</th>
                  <th>Finished</th>
                  <th>Duración</th>
                  <th>Status</th>
                  <th>Rows</th>
                  <th>Host</th>
                  <th>Commit</th>
                </tr>
              </thead>
              <tbody>
                {data.map(r => (
                  <tr key={r.id}>
                    <td className="etl-script">{r.script_name}</td>
                    <td>
                      <div>{r.triggered_by || '—'}</div>
                      {r.triggered_by_email && (
                        <div className="etl-sub">{r.triggered_by_email}</div>
                      )}
                    </td>
                    <td className="etl-num">{fmtDateTime(r.started_at)}</td>
                    <td className="etl-num">{fmtDateTime(r.finished_at)}</td>
                    <td className="etl-num">{calcDuration(r.started_at, r.finished_at)}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="etl-num">
                      {r.rows_affected != null
                        ? Number(r.rows_affected).toLocaleString('es-AR')
                        : '—'}
                    </td>
                    <td className="etl-sub">{r.host || '—'}</td>
                    <td className="etl-commit">
                      {r.git_commit ? String(r.git_commit).slice(0, 7) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalOpen && (
        <RegisterRunModal
          defaultEmail={currentEmail}
          knownScripts={Array.from(new Set([...KNOWN_SCRIPTS, ...uniqueScriptsInData]))}
          busy={modalBusy}
          onClose={() => modalBusy ? null : setModalOpen(false)}
          onSubmit={handleRegister}
        />
      )}

      <ToastBanner toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
